import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { MessageType, MessageStatus, SenderType } from '@constants/inbox';
import { getFullName } from '@helpers/userName';
import MessageContent from './MessageContent';

const MessageWrapper = styled('div', {
    shouldForwardProp: prop =>
        prop !== 'type' &&
        prop !== 'multiple' &&
        prop !== 'isLast' &&
        prop !== 'contentType' &&
        prop !== 'withReply' &&
        prop !== 'senderType' &&
        prop !== 'messageStatus',
})(
    ({ type, multiple, isLast, senderType, messageStatus }) => `
    padding: 12px;
    width: fit-content;
    background-color: ${
        type === MessageType.Outgoing
            ? messageStatus === MessageStatus.Failed
                ? 'rgba(176, 0, 32, 0.25)'
                : '#EBF6F0'
            : '#F2F2F2'
    };
    border-radius: ${type === MessageType.Outgoing ? '8px 8px 0px 8px' : '8px 8px 8px 0px'};
    background-color: ${type === MessageType.Outgoing && senderType === SenderType.Bot && '#E4EAEB'};
    & svg > path {
        fill: rgba(0, 0, 0, 0.87);
    }
    ${
        multiple &&
        `
        border-radius: 8px;
        &:not(:last-child) {
            margin-bottom: 4px;
        }
    `
    }
    ${
        multiple &&
        isLast &&
        `
        border-radius: ${type === MessageType.Outgoing ? '8px 8px 0 8px' : '8px 8px 8px 0'};
    `
    }
`,
);

const ReplyWrapper = styled('div')`
    padding-left: 5px;
    border-left: 2px solid rgba(0, 0, 0, 0.12);
    margin-bottom: 6px;
`;

const MessageContentWrapper = ({
    data,
    type,
    isLast,
    multiple,
    users,
    locations,
    assignedLocation,
    isOutgoing,
    messageStatus,
}) => {
    const senderName = useMemo(() => {
        if (!data.replyMessage) return '';
        const user = users.find(user => user.id === data.replyMessage.senderId);
        return getFullName(user?.firstName, user?.lastName);
    }, [data.replyMessage?.senderId, users]);

    const internalName = useMemo(() => {
        if (!data.replyMessage) return '';
        return locations.find(l => l.companyId === assignedLocation)?.internalName;
    }, [data.replyMessage?.companyId, locations]);

    const withReply = !!data.replyMessage;

    return (
        <MessageWrapper
            type={type}
            multiple={multiple}
            isLast={isLast}
            senderType={data.senderType}
            messageStatus={messageStatus}
        >
            {withReply ? (
                <ReplyWrapper>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.60)', marginBottom: '5px', display: 'block' }}
                    >
                        {isOutgoing ? senderName : internalName}
                    </Typography>
                    <MessageContent data={data.replyMessage} isOutgoing={isOutgoing} />
                </ReplyWrapper>
            ) : null}
            <MessageContent data={data} isOutgoing={isOutgoing} />
        </MessageWrapper>
    );
};

export default MessageContentWrapper;
